/**
 * SVG weather icons — work on Raspberry Pi Chromium (no color-emoji required).
 * Replaces emoji that render as white boxes on Pi.
 */
type Props = {
  name?: string;
  size?: number;
  className?: string;
  title?: string;
};

export function WeatherIcon({
  name = 'cloud',
  size = 36,
  className = '',
  title,
}: Props) {
  const common = {
    width: size,
    height: size,
    viewBox: '0 0 48 48',
    fill: 'none',
    xmlns: 'http://www.w3.org/2000/svg',
    className,
    role: 'img' as const,
    'aria-label': title || name,
  };

  switch (name) {
    case 'sun':
      return (
        <svg {...common}>
          <circle cx="24" cy="24" r="9" fill="#FBBF24" />
          {[0, 45, 90, 135, 180, 225, 270, 315].map((deg) => (
            <rect
              key={deg}
              x="22.5"
              y="3"
              width="3"
              height="7"
              rx="1.5"
              fill="#F59E0B"
              transform={`rotate(${deg} 24 24)`}
            />
          ))}
        </svg>
      );
    case 'cloud-sun':
      return (
        <svg {...common}>
          <circle cx="18" cy="18" r="7" fill="#FBBF24" />
          <path
            d="M14 34h18a7 7 0 0 0 0-14 9 9 0 0 0-17.2-2.5A6.5 6.5 0 0 0 14 34z"
            fill="#94A3B8"
          />
          <path
            d="M16 32h16a5 5 0 0 0 0-10 7.5 7.5 0 0 0-14.2-2A5 5 0 0 0 16 32z"
            fill="#CBD5E1"
          />
        </svg>
      );
    case 'cloud':
      return (
        <svg {...common}>
          <path
            d="M12 34h22a8 8 0 0 0 0-16 10 10 0 0 0-19.2-3A7 7 0 0 0 12 34z"
            fill="#94A3B8"
          />
          <path
            d="M14 32h20a6 6 0 0 0 0-12 8.5 8.5 0 0 0-16.1-2.2A5.5 5.5 0 0 0 14 32z"
            fill="#CBD5E1"
          />
        </svg>
      );
    case 'cloud-fog':
      return (
        <svg {...common}>
          <path
            d="M14 22h18a6 6 0 0 0 0-12 8 8 0 0 0-15.2-2A5 5 0 0 0 14 22z"
            fill="#94A3B8"
          />
          <rect x="10" y="28" width="28" height="3" rx="1.5" fill="#64748B" />
          <rect x="12" y="34" width="24" height="3" rx="1.5" fill="#64748B" />
          <rect x="14" y="40" width="20" height="3" rx="1.5" fill="#64748B" />
        </svg>
      );
    case 'cloud-drizzle':
    case 'cloud-rain':
      return (
        <svg {...common}>
          <path
            d="M12 26h22a7 7 0 0 0 0-14 9 9 0 0 0-17.2-2.5A6 6 0 0 0 12 26z"
            fill="#64748B"
          />
          <path d="M16 32l-2 8" stroke="#38BDF8" strokeWidth="2.5" strokeLinecap="round" />
          <path d="M24 30l-2 10" stroke="#38BDF8" strokeWidth="2.5" strokeLinecap="round" />
          <path d="M32 32l-2 8" stroke="#38BDF8" strokeWidth="2.5" strokeLinecap="round" />
        </svg>
      );
    case 'snow':
      return (
        <svg {...common}>
          <path
            d="M12 26h22a7 7 0 0 0 0-14 9 9 0 0 0-17.2-2.5A6 6 0 0 0 12 26z"
            fill="#94A3B8"
          />
          <circle cx="16" cy="34" r="2" fill="#E2E8F0" />
          <circle cx="24" cy="38" r="2" fill="#E2E8F0" />
          <circle cx="32" cy="34" r="2" fill="#E2E8F0" />
          <circle cx="20" cy="42" r="1.5" fill="#E2E8F0" />
          <circle cx="28" cy="42" r="1.5" fill="#E2E8F0" />
        </svg>
      );
    case 'cloud-lightning':
      return (
        <svg {...common}>
          <path
            d="M12 24h22a7 7 0 0 0 0-14 9 9 0 0 0-17.2-2.5A6 6 0 0 0 12 24z"
            fill="#475569"
          />
          <path d="M26 26l-6 10h6l-2 10 10-14h-6l4-6z" fill="#FBBF24" />
        </svg>
      );
    default:
      return (
        <svg {...common}>
          <circle cx="24" cy="24" r="10" fill="#38BDF8" opacity="0.35" />
          <circle cx="24" cy="24" r="6" fill="#38BDF8" />
        </svg>
      );
  }
}
